import React, {
  FormEventHandler,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ListFrameworkRequirementGroupsRequest } from '@trustero/trustero-api-web/lib/model/framework_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
  DeleteButton,
} from 'src/components/Reusable/Buttons'
import { FlexAlign, FlexColumn, FlexRow } from 'src/components/Reusable/Flex'
import { TextInput } from 'src/components/Reusable/Inputs/TextInput/TextInput'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { useControls } from 'src/components/async/model/control/useControls'
import { useFrameworks } from 'src/components/async/Framework/useFrameworks'
import { useComplianceFrameworks } from 'src/components/async/complianceframework/useComplianceFrameworks'
import { FrameworksAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { createEmptyRequirementDraft } from 'src/pages/Frameworks/frameworks.helpers'
import { FrameworkWizardGroup } from 'src/pages/Frameworks/frameworks.types'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import { useCreateCustomFrameworkWithRequirements } from 'src/pages/Frameworks/frameworks.hooks'
import { ModalForm, ModalFormId } from '../..'
import { parseDuplicateRequirementNameError } from './FrameworkRequirementModals.helpers'
import {
  RequirementCard,
  RequirementCardHeader,
  RequirementCardTitle,
  RequirementDescriptionInput,
  RequirementField,
  RequirementFieldRow,
  RequirementFields,
  RequirementsList,
} from './AddFrameworkRequirementsModal.styles'

const REQUIREMENT_NAME_MAX = 255
const REQUIREMENT_DESCRIPTION_MAX = 2000

export const AddFrameworkRequirementsModal = (): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_FRAMEWORK_REQUIREMENTS)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_FRAMEWORK_REQUIREMENTS,
  })
  const { openModal } = useModalState()
  const navigate = useNavigate()
  const { pageContext } = useParams()
  const submitWithRequirements = useCreateCustomFrameworkWithRequirements()
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const {
    name,
    description,
    requirementDrafts,
    setRequirementDrafts,
    groups,
    setGroups,
    appendComplianceFrameworkModelId,
    reset,
  } = useCustomFrameworkWizard()
  const primarySubmitLabel = appendComplianceFrameworkModelId
    ? 'Add requirements'
    : 'Create framework'
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show) return
    setRequirementDrafts((prev) =>
      prev.length > 0 ? prev : [createEmptyRequirementDraft()],
    )
  }, [show, setRequirementDrafts])

  useEffect(() => {
    if (!show || !appendComplianceFrameworkModelId) return

    let cancelled = false
    ;(async () => {
      try {
        const resp = await modelClient.listFrameworkRequirementGroups(
          new ListFrameworkRequirementGroupsRequest().setComplianceFrameworkModelId(
            appendComplianceFrameworkModelId,
          ),
        )
        if (cancelled) return
        const list = resp.getGroupsList()
        setGroups(
          list.map((row) => ({
            modelId: row.getModelId(),
            name: row.getName(),
            description: row.getDescription(),
          })) satisfies FrameworkWizardGroup[],
        )
      } catch (err) {
        if (!cancelled) {
          const errorMessage = getErrorMessage(err as RpcError)
          toast.error(errorMessage.message)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [show, appendComplianceFrameworkModelId, modelClient, setGroups])

  const { data: controlsData, isLoading: controlsLoading } = useControls(
    undefined,
    show,
    undefined,
    undefined,
    false,
    true,
  )
  const { data: frameworksData } = useFrameworks()
  const { data: complianceFrameworksData } = useComplianceFrameworks()

  const appendComplianceFrameworkId = useMemo(() => {
    if (!appendComplianceFrameworkModelId) return ''
    return (
      (complianceFrameworksData?.getItemsList() ?? [])
        .find(
          (framework) =>
            framework.getModelId() === appendComplianceFrameworkModelId,
        )
        ?.getId() ?? ''
    )
  }, [complianceFrameworksData, appendComplianceFrameworkModelId])

  const existingRequirementNames = useMemo(() => {
    if (!appendComplianceFrameworkId) return new Set<string>()
    return new Set(
      (frameworksData?.getItemsList() ?? [])
        .filter(
          (f) => f.getComplianceFrameworkId() === appendComplianceFrameworkId,
        )
        .map((f) => f.getName().trim().toLowerCase())
        .filter(Boolean),
    )
  }, [frameworksData, appendComplianceFrameworkId])

  const hasControlLinks = requirementDrafts.some((r) => r.controlIds.length > 0)
  const submitBlockedByControls = hasControlLinks && controlsLoading

  const controlItems = useMemo<SelectItem[]>(
    () =>
      (controlsData?.getItemsList() ?? []).map((c) => ({
        name: `${c.getModelId()} ${c.getName()}`,
        value: c.getId(),
      })),
    [controlsData],
  )

  const groupItems = useMemo<SelectItem[]>(
    () =>
      groups.map((g) => ({
        name: g.name,
        value: g.modelId ?? g.name,
      })),
    [groups],
  )
  const hasGroups = groupItems.length > 0

  const closeAndResetWizard = () => {
    reset()
    setRowErrors({})
    hide()
  }

  const onBack = () => {
    setRowErrors({})
    hide()
    openModal(ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS)
  }

  const onOpenGroupsModal = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    openModal(ModalFormId.ADD_FRAMEWORK_GROUPS)
  }

  const addRow = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setRequirementDrafts((prev) => [...prev, createEmptyRequirementDraft()])
  }

  const removeRow = (tempId: string) => {
    setRequirementDrafts((prev) => {
      if (prev.length <= 1) return [createEmptyRequirementDraft()]
      return prev.filter((r) => r.tempId !== tempId)
    })
    setRowErrors((prev) => {
      if (!prev[tempId]) return prev
      const copy = { ...prev }
      delete copy[tempId]
      return copy
    })
  }

  const updateRow = (
    tempId: string,
    patch: Partial<(typeof requirementDrafts)[0]>,
  ) => {
    setRequirementDrafts((prev) =>
      prev.map((r) => (r.tempId === tempId ? { ...r, ...patch } : r)),
    )
    if (
      rowErrors[tempId] &&
      typeof patch.name === 'string' &&
      patch.name.trim().length > 0
    ) {
      setRowErrors((prev) => {
        const copy = { ...prev }
        delete copy[tempId]
        return copy
      })
    }
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    const nextRowErrors: Record<string, string> = {}
    const seenNameToTempId = new Map<string, string>()
    requirementDrafts.forEach((r) => {
      const trimmed = r.name.trim()
      if (!trimmed) {
        nextRowErrors[r.tempId] = 'Enter a requirement name.'
        return
      }
      const key = trimmed.toLowerCase()
      if (existingRequirementNames.has(key)) {
        nextRowErrors[r.tempId] =
          'A requirement with this name already exists in this framework.'
        return
      }
      const firstTempId = seenNameToTempId.get(key)
      if (firstTempId) {
        nextRowErrors[r.tempId] =
          'Another requirement in this form already uses this name.'
        if (!nextRowErrors[firstTempId]) {
          nextRowErrors[firstTempId] =
            'Another requirement in this form already uses this name.'
        }
        return
      }
      seenNameToTempId.set(key, r.tempId)
    })
    if (Object.keys(nextRowErrors).length) {
      setRowErrors(nextRowErrors)
      return
    }
    setIsSubmitting(true)
    try {
      const { complianceFrameworkRecordId } = await submitWithRequirements({
        name: name.trim(),
        description,
        requirements: requirementDrafts.map((r) => ({
          name: r.name.trim(),
          category: r.category.trim(),
          groupDescription: r.groupDescription.trim(),
          description: r.description.trim(),
          controlIds: r.controlIds,
        })),
        appendComplianceFrameworkModelId,
        groups,
      })
      closeAndResetWizard()
      navigate(
        `/${pageContext}/${FrameworksAbsoluteRoutes.SHOW}/${complianceFrameworkRecordId}`,
      )
    } catch (err) {
      const rpcErr = err as RpcError
      const dupInfo = parseDuplicateRequirementNameError(rpcErr?.message ?? '')
      if (dupInfo) {
        const conflictKey = dupInfo.name.toLowerCase()
        const conflictingDrafts = requirementDrafts.filter(
          (r) => r.name.trim().toLowerCase() === conflictKey,
        )
        if (conflictingDrafts.length > 0) {
          const rowErrorMessage = dupInfo.isExisting
            ? 'A requirement with this name already exists in this framework.'
            : 'Another requirement in this form already uses this name.'
          setRowErrors((prev) => ({
            ...prev,
            ...Object.fromEntries(
              conflictingDrafts.map((r) => [r.tempId, rowErrorMessage]),
            ),
          }))
        } else {
          toast.error(
            dupInfo.isExisting
              ? `A requirement named "${dupInfo.name}" already exists in this framework.`
              : `Duplicate requirement name "${dupInfo.name}".`,
          )
        }
      } else {
        const errorMessage = getErrorMessage(rpcErr)
        toast.error(errorMessage.message)
        if (errorMessage.shouldLog) {
          log.error('AddFrameworkRequirementsModal submit failed', err)
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndResetWizard}
      onBack={onBack}
      formId={ModalFormId.ADD_FRAMEWORK_REQUIREMENTS}
      size="xl"
      title="Add requirements"
      customSubmitButton={
        <StandardButton
          type="submit"
          form={ModalFormId.ADD_FRAMEWORK_REQUIREMENTS}
          variant={StandardButtonVariant.PRIMARY}
          buttonSize={StandardButtonSize.MEDIUM}
          disabled={isSubmitting || submitBlockedByControls}
        >
          {primarySubmitLabel}
        </StandardButton>
      }
      showOverflow
    >
      <form id={ModalFormId.ADD_FRAMEWORK_REQUIREMENTS} onSubmit={onSubmit}>
        <FlexColumn gap={16} align={FlexAlign.FLEX_START} isFullWidth>
          <FlexRow
            isFullWidth
            justify={FlexAlign.SPACE_BETWEEN}
            align={FlexAlign.CENTER}
            gap={16}
          >
            <p>Add framework requirements.</p>
          </FlexRow>
          <RequirementsList>
            {requirementDrafts.map((row, index) => {
              const selectedControls = controlItems.filter((c) =>
                row.controlIds.includes(String(c.value)),
              )
              const selectedGroup = groupItems.find(
                (g) => g.value === row.category,
              )

              return (
                <RequirementCard key={row.tempId}>
                  <RequirementCardHeader>
                    <RequirementCardTitle>
                      Requirement {index + 1}
                    </RequirementCardTitle>
                    <DeleteButton
                      aria-label="Remove requirement"
                      onClick={() => removeRow(row.tempId)}
                    />
                  </RequirementCardHeader>
                  <RequirementFields>
                    <TextInput
                      name={`requirement-name-${row.tempId}`}
                      initVal={row.name}
                      label="Requirement name"
                      placeholder="Enter requirement name"
                      onChangeCb={(v) =>
                        updateRow(row.tempId, {
                          name: v.slice(0, REQUIREMENT_NAME_MAX),
                        })
                      }
                      errorMessage={rowErrors[row.tempId]}
                      hasError={!!rowErrors[row.tempId]}
                      showErrorMessage={!!rowErrors[row.tempId]}
                      allowEmptyVal
                    />
                    <RequirementFieldRow>
                      <RequirementField>
                        <SelectDropdown
                          label="Group"
                          labelButton={
                            <TextButton onClick={onOpenGroupsModal}>
                              Add groups
                            </TextButton>
                          }
                          placeholder={
                            hasGroups ? 'Select group' : 'Add groups first'
                          }
                          items={groupItems}
                          defaultValues={selectedGroup ? [selectedGroup] : []}
                          onSelectCb={(items) =>
                            updateRow(row.tempId, {
                              category: items[0] ? String(items[0].value) : '',
                            })
                          }
                          hideError
                          isDisabled={!hasGroups}
                        />
                      </RequirementField>
                      <RequirementField>
                        <SelectDropdown
                          key={`${row.tempId}-${row.controlIds.join(',')}`}
                          label="Link controls"
                          placeholder="Select controls"
                          items={controlItems}
                          defaultValues={selectedControls}
                          isMultiSelect
                          onSelectCb={(items) =>
                            updateRow(row.tempId, {
                              controlIds: items.map((i) => String(i.value)),
                            })
                          }
                          hideError
                        />
                      </RequirementField>
                    </RequirementFieldRow>
                    <RequirementDescriptionInput
                      name={`requirement-description-${row.tempId}`}
                      initVal={row.description}
                      label="Description (optional)"
                      placeholder="Add details about this requirement"
                      maxInputLength={REQUIREMENT_DESCRIPTION_MAX}
                      customOnChange={(
                        e: React.ChangeEvent<HTMLTextAreaElement>,
                      ) =>
                        updateRow(row.tempId, {
                          description: e.target.value.slice(
                            0,
                            REQUIREMENT_DESCRIPTION_MAX,
                          ),
                        })
                      }
                    />
                  </RequirementFields>
                </RequirementCard>
              )
            })}
          </RequirementsList>
          <FlexRow justify={FlexAlign.CENTER} isFullWidth>
            <StandardButton
              variant={StandardButtonVariant.SECONDARY}
              buttonSize={StandardButtonSize.MEDIUM}
              onClick={addRow}
            >
              Add Requirement Row
            </StandardButton>
          </FlexRow>
        </FlexColumn>
      </form>
    </ModalForm>
  )
}
